import axios from '../helpers/axios';
import { logError } from '../services';
import {
  IResponse,
  IFieldWalkthrough,
  IFieldWalkthroughLocation,
  INewFieldWalkthroughOptions,
  IFieldWalkthroughTerritory,
  IFieldWalkthroughDetail,
  IDropdownResponse,
  IUpdateFieldWalkthroughOptions,
  IFieldWalkthroughDefaultMarkup,
  IUnassignedFieldWalkthroughItem,
} from '../models';

const baseUrl = `/api/field-walkthroughs`;

export const getFieldWalkthroughs = async (filters?: {
  page?: number;
  perPage?: number | string;
  sortBy?: string;
  sortDirection?: string;
  planDescription?: string;
  territoryId?: string | number;
  neighborhoodId?: string | number;
  lotNumber?: string | number;
  createdOnStart?: string;
  createdOnEnd?: string;
  createdBy?: string;
  showMine?: boolean;
  isComplete?: boolean;
}): Promise<IResponse<IFieldWalkthrough[]>> => {
  try {
    const { data } = await axios.get(baseUrl, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getFieldWalkthroughs failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getFieldWalkthroughStatuses = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`${baseUrl}/field-walkthrough-statuses`);
    return data;
  } catch (error) {
    logError(error, 'getFieldWalkthroughStatuses failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getFieldWalkthroughTerritories = async (): Promise<IFieldWalkthroughTerritory[]> => {
  try {
    const { data } = await axios.get(`${baseUrl}/territories`);
    return data;
  } catch (error) {
    logError(error, 'getFieldWalkthroughTerritories failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getFieldWalkthroughCreatedBy = async (): Promise<string[]> => {
  try {
    const { data } = await axios.get(`${baseUrl}/created-by`);
    return data;
  } catch (error) {
    logError(error, 'getFieldWalkthroughCreatedBy failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createFieldWalkthrough = async (options: INewFieldWalkthroughOptions) => {
  try {
    const result = await axios.post(baseUrl, options);
    return result.data;
  } catch (error) {
    logError(
      {
        error,
        payload: {
          ...options
        }
      },
      'createFieldWalkthrough failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const updateFieldWalkthrough = async (
  fieldWalkthroughId: string,
  options: IUpdateFieldWalkthroughOptions
) => {
  try {
    const result = await axios.put(`${baseUrl}/${fieldWalkthroughId}`, options);
    return result.data;
  } catch (error) {
    logError(
      {
        error,
        payload: {
          ...options
        }
      },
      'createFieldWalkthrough failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const updateFieldWalkthroughItemPhoto = async (
  fieldWalkthroughId: string,
  fieldWalkthroughItemId: number,
  file: File
) => {
  try {
    const formData = new FormData();
    formData.append('Image', file);
    const result = await axios.post(
      `${baseUrl}/${fieldWalkthroughId}/items/${fieldWalkthroughItemId}/image`,
      formData
    );
    return result.data;
  } catch (error) {
    logError(
      {
        error,
        payload: {
          fieldWalkthroughId,
          fieldWalkthroughItemId
        }
      },
      'updateFieldWalkthroughItemPhoto failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const getFieldWalkthroughLocations = async (
  id: string
): Promise<IFieldWalkthroughLocation[]> => {
  try {
    const { data } = await axios.get(`${baseUrl}/${id}/locations`);
    return data;
  } catch (error) {
    logError(error, 'getFieldWalkthroughLocations failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getFieldWalkthroughDetail = async (
  id: string,
  locationId: number
): Promise<IFieldWalkthroughDetail> => {
  try {
    const { data } = await axios.get(`${baseUrl}/${id}`, {
      params: {
        locationId: locationId === -1 ? null : locationId
      }
    });
    return data;
  } catch (error) {
    logError(error, 'getFieldWalkthroughDetail failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getFieldWalkthroughDetailItemImage = async (
  id: string,
  fieldWalkthroughItemId: string
): Promise<IFieldWalkthroughDetail> => {
  try {
    const { data } = await axios.get(`${baseUrl}/${id}/items/${fieldWalkthroughItemId}/image`);
    return data;
  } catch (error) {
    logError(error, 'getFieldWalkthroughDetail failed');
    return Promise.reject(error?.response?.data);
  }
};

export const generateDeficiencyReport = async (id: string): Promise<any> => {
  try {
    const result = await axios.post(`${baseUrl}/${id}/deficiency-report`);
    return result;
  } catch (error) {
    logError(
      {
        error,
        id
      },
      'generateDeficiencyReport failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const sendDeficiencyReport = async (id: string, emailAddress: string) => {
  try {
    const result = await axios.post(`${baseUrl}/${id}/deficiency-report-email`, {
      emailAddress
    });
    return result.data;
  } catch (error) {
    logError(
      {
        error,
        id
      },
      'sendDeficiencyReport failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const getFieldWalkthroughDeficiencyReport = async (id: string | number): Promise<any> => {
  try {
    const { data } = await axios.get(`${baseUrl}/${id}/deficiency-report`, {
      responseType: 'blob'
    });
    return data;
  } catch (error) {
    logError(error, 'getFieldWalkthroughDeficiencyReport failed');
    return Promise.reject(error?.response?.data);
  }
};

export const verifyWalkthroughCompletion = async (id: string, formData: FormData) => {
  try {
    const result = await axios.put(`${baseUrl}/${id}/complete`, formData);
    return result.data;
  } catch (error) {
    logError(
      {
        error,
        id
      },
      'verifyWalkthroughCompletion failed'
    );
    throw error;
  }
};

export const resetFieldWalkthrough = async (id: string | number) => {
  try {
    const result = await axios.put(`${baseUrl}/${id}/reset`);
    return result.data;
  } catch (error) {
    logError(
      {
        error,
        id
      },
      'resetFieldWalkthrough failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const deleteFieldWalkthrough = async (id: string | number) => {
  try {
    const result = await axios.delete(`${baseUrl}/${id}`);
    return result.data;
  } catch (error) {
    logError(
      {
        error,
        id
      },
      'deleteFieldWalkthrough failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const FIELD_WALKTHROUGH_DEFAULT_MARKUPS_QUERY_KEY = 'get-field-walkthrough-default-markups';
export const getFieldWalkthroughDefaultMarkups = async (
  id: string | number
): Promise<IFieldWalkthroughDefaultMarkup[]> => {
  try {
    const { data } = await axios.get(`${baseUrl}/${id}/default-markups`);
    return data;
  } catch (error) {
    logError(error, 'getFieldWalkthroughDefaultMarkups failed');
    return Promise.reject(error?.response?.data);
  }
};

export const CREATE_FIELD_WALKTHROUGHT_MARKUPS_QUERY_KEY =
  'create-field-walkthrough-default-markups';
export const createFieldWalkthroughItemMarkups = async (
  fieldWalkthroughItemId: string | number,
  payload: FormData
): Promise<number> => {
  try {
    const { data } = await axios.post(`${baseUrl}/items/${fieldWalkthroughItemId}/markup`, payload);
    return data;
  } catch (error) {
    logError(error, 'createFieldWalkthroughItemMarkups failed');
    return Promise.reject(error?.response?.data);
  }
};

export const UPDATE_FIELD_WALKTHROUGHT_MARKUPS_QUERY_KEY =
  'update-field-walkthrough-default-markups';
export const updateFieldWalkthroughItemMarkups = async (
  fieldWalkthroughItemId: string | number,
  payload: FormData
): Promise<IFieldWalkthroughDefaultMarkup[]> => {
  try {
    const { data } = await axios.put(
      `${baseUrl}/items/files/${fieldWalkthroughItemId}/markup`,
      payload
    );
    return data;
  } catch (error) {
    logError(error, 'updateFieldWalkthroughItemMarkups failed');
    return Promise.reject(error?.response?.data);
  }
};

export const DELETE_FIELD_WALKTHROUGHT_MARKUPS_QUERY_KEY =
  'delete-field-walkthrough-default-markups';
export const deleteFieldWalkthroughItemMarkup = async (
  fieldWalkthroughItemId: string | number
): Promise<any> => {
  try {
    const { data } = await axios.delete(`${baseUrl}/items/files/${fieldWalkthroughItemId}/markup`);
    return data;
  } catch (error) {
    logError(error, 'deleteFieldWalkthroughItemMarkup failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createUnassignedFieldWalkthroughItems = async (
  fieldWalkthroughId: string,
  items: IUnassignedFieldWalkthroughItem[]
) => {
  try {
    const result = await axios.post(
      `${baseUrl}/${fieldWalkthroughId}/unassigned/items`,
      { items }
    );
    return result.data;
  } catch (error) {
    logError(
      {
        error,
        payload: {
          fieldWalkthroughId,
          items
        }
      },
      'createUnassignedFieldWalkthroughItems failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const generateConsolidatedFloorPlan = async (
  fieldWalkthroughId: string,
  floorplanName: string
) => {
  try {
    const { data } = await axios.get(
      `${baseUrl}/${fieldWalkthroughId}/consolidated-floorplan`,
      {
        params: { floorplanName }
      }
    );
    return data;
  } catch (error) {
    logError(
      {
        error,
        payload: {
          fieldWalkthroughId,
          floorplanName
        }
      },
      'generateConsolidatedFloorPlan failed'
    );
    return Promise.reject(error?.response?.data);
  }
};
